'use client';
import useTranslation from '../../context/Translation/useTranslation';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import Image from '@repo/ui/src/components/Image';
import {
  AmplienceButtonProps,
  ArticleModuleProps,
  MediaType,
  MetaProps,
  SeoProps,
} from '@repo/ui/src/types';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '@repo/ui/src/utils/classNames';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { getButtonVariant } from '@repo/ui/src/components/Button/utils';
import { appConfig } from '@repo/ui/src/utils/appConfig';

export interface LearnCardProps {
  _meta?: MetaProps;
  seo?: SeoProps;
  title?: string;
  SEO?: any;
  headerAsset?: Array<{
    image: {
      id: string;
      url: string;
    };
  }>;
  articleModules?: Array<ArticleModuleProps>;
  buttons?: Array<AmplienceButtonProps>;
  description?: string;
  backgroundColor?: string;
  backgroundImage?: MediaType;
  learnShopGrid?: boolean;
}

const getImage = (
  headerAsset: LearnCardProps['headerAsset'],
  seoObj: LearnCardProps['SEO'],
  articleModules: LearnCardProps['articleModules'],
  backgroundImage: LearnCardProps['backgroundImage'],
) => {
  if (headerAsset?.length) {
    return buildImageSrc(null, 600, 5, 4, headerAsset[0].image?.url);
  }
  if (seoObj?.ogImage) {
    return buildImageSrc(seoObj.ogImage, 600, 5, 4);
  }
  if (articleModules?.[0]?.image) {
    return buildImageSrc(articleModules[0].image, 600, 5, 4);
  }
  if (backgroundImage) {
    return buildImageSrc(backgroundImage, 800, 1, 1);
  }
  return buildImageSrc(null, 600, 5, 4);
};

const LearnCard = ({
  _meta,
  SEO,
  seo = SEO,
  title,
  headerAsset,
  articleModules,
  buttons,
  description,
  backgroundColor,
  backgroundImage,
  learnShopGrid,
}: LearnCardProps) => {
  const background =
    backgroundColor === 'Dark' || backgroundColor === 'Black'
      ? 'bg-neutral-700'
      : 'bg-white border border-warm-300';
  const fontColor =
    backgroundColor === 'Dark' || backgroundColor === 'Black'
      ? 'text-neutral-100 hover:text-white'
      : 'text-neutral-600 hover:text-neutral-700';

  const dark = backgroundColor === 'Dark' || backgroundColor === 'Black';

  const { t } = useTranslation();
  const linkObj = _meta && buildLinkObj({ link: _meta?.deliveryKey });

  return (
    <div
      className={classNames('h-full grid grid-rows-[auto_1fr_auto] w-full', background)}
      role="group"
      aria-label="article"
    >
      {!learnShopGrid && (
        <Anchor
          link={linkObj?.href}
          className="absolute top-0 left-0 w-full h-full z-10"
          tabIndex={-1}
        />
      )}
      <div className="relative h-[250px]">
        <Image
          src={getImage(headerAsset, seo, articleModules, backgroundImage)}
          alt=""
          layout="fill"
          objectFit="cover"
          className="object-contain"
        />
      </div>
      <div className="pt-8 px-5">
        {title && (
          <h4 className={classNames('tra-text-web-heading-6 text-left mb-2', fontColor)}>
            {title}
          </h4>
        )}
        {(seo?.ogDescription || description) && (
          <>
            <hr className="border-2 w-10 border-brand-300 my-4" />
            <p className={classNames('line-clamp-8 md:line-clamp-3', fontColor)}>
              {learnShopGrid ? description : seo.ogDescription}
            </p>
          </>
        )}
      </div>
      <div className="p-5 flex gap-5">
        {learnShopGrid &&
          buttons &&
          buttons?.map((button, i) => (
            <Button
              {...button?.link}
              variant={getButtonVariant(button.style, button.icon)}
              as={Anchor}
              className="z-20 pr-6"
              key={i}
              dark={dark}
              size={appConfig.isTraeger ? 'small' : 'medium'}
            >
              {button?.link?.displayName}
            </Button>
          ))}
        {!learnShopGrid && (
          <Button
            link={linkObj?.href}
            as={Anchor}
            variant={ButtonVariant.knife}
            className="z-20"
            size="small"
          >
            {t('genericContent.learnMore', undefined, {
              default: 'Learn More',
            })}
          </Button>
        )}
      </div>
    </div>
  );
};

export default LearnCard;
